import { useState } from 'react';
import { Alert, Button, Snackbar } from '@mui/material';
import { useGetOrCreateExternalFeedbackLinkMutation } from '../api/employeeReviewApi';
import { extractErrorMessage } from '../../../utils/errorUtils';

type Props = {
  row: Record<string, unknown>;
  reviewId?: string;
};

const copyText = async (value: string) => {
  if (navigator.clipboard && window.isSecureContext) {
    await navigator.clipboard.writeText(value);
    return;
  }

  const textarea = document.createElement('textarea');
  textarea.value = value;
  textarea.style.position = 'fixed';
  textarea.style.opacity = '0';
  document.body.appendChild(textarea);
  textarea.select();
  const copied = document.execCommand('copy');
  textarea.remove();
  if (!copied) throw new Error('Unable to copy the feedback link.');
};

export const CopyExternalFeedbackLinkCell = ({ row, reviewId }: Props) => {
  const [getLink, { isLoading }] = useGetOrCreateExternalFeedbackLinkMutation();
  const [notice, setNotice] = useState<{ message: string; severity: 'success' | 'error' } | null>(
    null
  );
  const participantId = Number(row.Id ?? row.HrmEmployeeReviewParticipantId);
  const isExternal = row.CrmRelationId == null || String(row.CrmRelationId).trim() === '';

  if (!isExternal) return null;

  const handleCopy = async () => {
    const numericReviewId = Number(reviewId);
    if (
      !Number.isInteger(numericReviewId) ||
      numericReviewId <= 0 ||
      !Number.isInteger(participantId)
    ) {
      setNotice({ message: 'External feedback link is not available.', severity: 'error' });
      return;
    }

    try {
      const result = await getLink({ reviewId: numericReviewId, participantId }).unwrap();
      await copyText(result.feedbackUrl);
      setNotice({ message: 'Feedback link copied.', severity: 'success' });
    } catch (error) {
      setNotice({
        message: extractErrorMessage(error, 'Failed to copy the feedback link.'),
        severity: 'error',
      });
    }
  };

  return (
    <>
      <Button size="small" onClick={handleCopy} disabled={isLoading}>
        Copy Link
      </Button>
      <Snackbar
        open={notice != null}
        autoHideDuration={4000}
        onClose={() => setNotice(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity={notice?.severity ?? 'success'} onClose={() => setNotice(null)}>
          {notice?.message}
        </Alert>
      </Snackbar>
    </>
  );
};
